/**
 * 키와 pointer 를 하나의 주행 입력으로 합친다.
 *
 * 물리 키 단위로 눌림을 기억한다 — W 와 ArrowUp 을 함께 눌렀다가 하나만 놓아도
 * 남은 키의 방향이 유지돼야 하기 때문이다. 화면 방향 버튼은 pointerId 별로
 * 따로 기억하고, 키와는 방향별 OR 로 합친다.
 */
import type { WebCarEngine } from "../engine/web-car-engine";

export type Direction = "forward" | "back" | "left" | "right";

export const KEY_DIRECTIONS: Record<string, Direction> = {
  KeyW: "forward",
  ArrowUp: "forward",
  KeyS: "back",
  ArrowDown: "back",
  KeyA: "left",
  ArrowLeft: "left",
  KeyD: "right",
  ArrowRight: "right",
};

export interface DriveVector {
  throttle: number;
  steering: number;
}

/** 순수 계산 — DOM 없이 시험할 수 있다. */
export class DirectionState {
  private keys = new Set<string>();
  private pointers = new Map<number, Direction>();

  /** 처리한 키면 true (그때만 preventDefault 한다). */
  keyDown(code: string): boolean {
    const direction = KEY_DIRECTIONS[code];
    if (!direction) return false;
    this.keys.add(code);
    return true;
  }

  keyUp(code: string): boolean {
    if (!(code in KEY_DIRECTIONS)) return false;
    this.keys.delete(code);
    return true;
  }

  pointerDown(pointerId: number, direction: Direction): void {
    this.pointers.set(pointerId, direction);
  }

  /** 그 pointer 만 뗀다. */
  pointerUp(pointerId: number): void {
    this.pointers.delete(pointerId);
  }

  /** pointercancel · lostpointercapture — 전부 뗀다. */
  clearPointers(): void {
    this.pointers.clear();
  }

  clearKeys(): void {
    this.keys.clear();
  }

  clearAll(): void {
    this.clearKeys();
    this.clearPointers();
  }

  private has(direction: Direction): boolean {
    for (const code of this.keys) {
      if (KEY_DIRECTIONS[code] === direction) return true;
    }
    for (const value of this.pointers.values()) {
      if (value === direction) return true;
    }
    return false;
  }

  get value(): DriveVector {
    const throttle = Number(this.has("forward")) - Number(this.has("back"));
    const steering = Number(this.has("right")) - Number(this.has("left"));
    return { throttle, steering };
  }

  get isIdle(): boolean {
    const { throttle, steering } = this.value;
    return throttle === 0 && steering === 0;
  }

  get pressedKeys(): string[] {
    return [...this.keys];
  }
}

/** 글자를 입력하는 중이면 조종 단축키를 가로채지 않는다. */
export function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA";
}

export interface ControllerOptions {
  engine: WebCarEngine;
  /** active 동안 입력을 다시 보내는 주기 */
  repeatMs?: number;
  onError?: (error: unknown) => void;
}

export class InputController {
  readonly state = new DirectionState();
  private readonly engine: WebCarEngine;
  private readonly repeatMs: number;
  private readonly onError: (error: unknown) => void;
  private timer: ReturnType<typeof setInterval> | null = null;
  private detachers: Array<() => void> = [];

  constructor(options: ControllerOptions) {
    this.engine = options.engine;
    this.repeatMs = options.repeatMs ?? 50;
    this.onError = options.onError ?? (() => {});
  }

  attach(): void {
    const on = <K extends keyof WindowEventMap>(
      target: Window | Document,
      type: K,
      handler: (event: WindowEventMap[K]) => void,
      options?: AddEventListenerOptions,
    ) => {
      target.addEventListener(type, handler as EventListener, options);
      this.detachers.push(() => target.removeEventListener(type, handler as EventListener));
    };

    on(window, "keydown", (event) => this.onKeyDown(event));
    on(window, "keyup", (event) => this.onKeyUp(event));
    on(window, "blur", () => this.onLeave("창이 포커스를 잃음"));
    on(window, "pagehide", () => this.engine.bestEffortStop());
    on(document, "visibilitychange" as keyof WindowEventMap, () => {
      if (document.hidden) this.onLeave("탭이 숨겨짐");
      else this.syncEnvironment();
    });
    on(window, "focus", () => this.syncEnvironment());
    on(document, "focusin" as keyof WindowEventMap, (event) => {
      // 글자 입력 칸으로 들어가면 잡고 있던 주행 입력을 먼저 0 으로 만든다.
      if (isTextEntry(event.target)) {
        this.state.clearAll();
        this.send();
      }
    });

    this.syncEnvironment();
    this.timer = setInterval(() => this.tick(), this.repeatMs);
  }

  detach(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    for (const off of this.detachers) off();
    this.detachers = [];
  }

  /** 화면 방향 버튼에서 부른다. */
  pointerDown(pointerId: number, direction: Direction): void {
    this.state.pointerDown(pointerId, direction);
    this.send();
  }

  pointerUp(pointerId: number): void {
    this.state.pointerUp(pointerId);
    this.send();
  }

  pointerCancel(): void {
    this.state.clearPointers();
    this.send();
    void this.engine.emergencyStop("포인터 입력이 취소됨").catch(this.onError);
  }

  private syncEnvironment(): void {
    this.engine.setEnvironment({ visible: !document.hidden, focused: document.hasFocus() });
  }

  private onLeave(reason: string): void {
    // 잡고 있던 키는 지운다. 다시 활성화하려면 방향키를 새로 눌렀다 놓아야 한다.
    this.state.clearAll();
    this.engine.setEnvironment({ visible: !document.hidden, focused: false });
    if (this.engine.currentState === "active") {
      void this.engine.emergencyStop(reason).catch(this.onError);
    }
  }

  private onKeyDown(event: KeyboardEvent): void {
    const connected = this.engine.currentState !== "idle" && this.engine.currentState !== "loading";

    // 비상정지와 해제는 글자 입력 중에도 연결 상태면 먼저 처리한다.
    if (event.code === "Space" && connected) {
      event.preventDefault();
      if (!event.repeat) {
        this.state.clearAll();
        this.send();
        void this.engine.emergencyStop("스페이스").catch(this.onError);
      }
      return;
    }
    if (event.code === "Escape" && connected) {
      event.preventDefault();
      if (!event.repeat) void this.engine.disconnect().catch(this.onError);
      return;
    }

    if (isTextEntry(event.target)) return;

    if (event.repeat) {
      // 방향키 repeat 는 무시한다 — 누름 상태는 이미 들고 있다.
      if (event.code in KEY_DIRECTIONS) event.preventDefault();
      return;
    }

    if (this.state.keyDown(event.code)) {
      event.preventDefault();
      this.send();
      return;
    }

    if (event.code === "Equal" || event.code === "NumpadAdd") {
      event.preventDefault();
      this.engine.adjustMaxSpeed(5);
      return;
    }
    if (event.code === "Minus" || event.code === "NumpadSubtract") {
      event.preventDefault();
      this.engine.adjustMaxSpeed(-5);
    }
  }

  private onKeyUp(event: KeyboardEvent): void {
    if (this.state.keyUp(event.code)) {
      event.preventDefault();
      this.send();
    }
  }

  private tick(): void {
    if (this.engine.currentState !== "active") return;
    if (document.hidden || !document.hasFocus()) return;
    this.send();
  }

  private send(): void {
    const { throttle, steering } = this.state.value;
    try {
      this.engine.setInput(throttle, steering, performance.now(), this.engine.inputGeneration);
    } catch (error) {
      this.onError(error);
    }
  }
}
